import type { DeepPartial } from '@halo/utils';
import { deepMerge } from '@halo/utils';
import { MessageAction, MessageStatus, MsgPayloadType } from '../sdk/types';
import type { Msg } from '../client/types/messages';

export function getIsSystemMessage(message?: Msg) {
  if (getIsMediaMessage(message)) {
    return true;
  }

  switch (message?.payload_type) {
    case MsgPayloadType.GROUP_CREATE:
    case MsgPayloadType.GROUP_UPDATE_NAME:
    case MsgPayloadType.GROUP_UPDATE_DESCRIPTION:
    case MsgPayloadType.GROUP_RESTRICTED:
    case MsgPayloadType.GROUP_UNRESTRICTED:
    case MsgPayloadType.GROUP_ADMIN_ONLY_SEND:
    case MsgPayloadType.GROUP_EXCEPTION_SEND:
    case MsgPayloadType.GROUP_RESTRICTED_MODE_ON:
    case MsgPayloadType.GROUP_RESTRICTED_MODE_OFF:
    case MsgPayloadType.GROUP_RESTRICTED_MODE_OFF_All:
    case MsgPayloadType.GROUP_SCREENSHOT_WARNING:
    case MsgPayloadType.GROUP_SCREEN_RECORDING_WARNING:
    case MsgPayloadType.GROUP_INVITE:
    case MsgPayloadType.GROUP_REMOVE_MEMBER:
    case MsgPayloadType.GROUP_MEMBER_QUIT:
    case MsgPayloadType.GROUP_MEDIA_CLOSE:
    case MsgPayloadType.GROUP_UPDATE_JOIN:
    case MsgPayloadType.GROUP_JOIN_SUCCESS:
    case MsgPayloadType.GROUP_TRANSFER_OWNER:
    case MsgPayloadType.GROUP_UPDATE_ADMINS:
    case MsgPayloadType.EXPIRE_DURATION:
    case MsgPayloadType.LUCKY_BOX_CLAIMED:
      return true;
    default:
      return false;
  }
}

export function getIsMediaMessage(message?: Msg) {
  switch (message?.payload_type) {
    case MsgPayloadType.MEDIA_AUDIO_CALL:
    case MsgPayloadType.MEDIA_VIDEO_CALL:
    case MsgPayloadType.CALL_HANG_UP:
    case MsgPayloadType.CALL_TIMEOUT:
    case MsgPayloadType.CALL_CANCELLED:
    case MsgPayloadType.CALL_REJECTED:
    case MsgPayloadType.CALL_BUSY:
    case MsgPayloadType.CALL_NETWORK_ERROR:
    case MsgPayloadType.CALL_PUT_THROUGH:
      return true;
    default:
      return false;
  }
}

export function isLuckyBoxMessage(message: Msg | undefined) {
  return message?.payload_type === MsgPayloadType.LUCKY_BOX;
}

/**
 * @deprecated
 * @see isLuckyBoxMessage
 */
export const isluckyBoxMessage = isLuckyBoxMessage;

export function isEmojiOnlyMessage(message: Pick<Msg, 'payload'> | undefined) {
  const emojiOnlyCount = message?.payload?.emojiOnlyCount;
  return 0 < emojiOnlyCount && emojiOnlyCount < 6;
}

export function isStickerMessage(message: Msg | null | undefined) {
  return message?.payload_type === MsgPayloadType.STICKER;
}

export function isTransferMessage(message: Msg | undefined) {
  return message?.payload_type === MsgPayloadType.TRANSFER;
}

export function isMyMessage(message: Msg | undefined, currentUserId: string | undefined) {
  return message?.owner_id === currentUserId;
}

/**
 * @deprecated
 * @see isMyMessage
 */
export const getIsMyMessage = isMyMessage;

export function isUserMsg(type: MsgPayloadType | undefined, includeMedia = true) {
  if (!type) {
    return false;
  }
  if (type >= MsgPayloadType.TEXT && type <= MsgPayloadType.FILE) {
    return true;
  }
  if (includeMedia) {
    if (type >= MsgPayloadType.MEDIA_AUDIO_CALL && type <= MsgPayloadType.CALL_PUT_THROUGH) {
      return true;
    }
  }
  if (type === MsgPayloadType.LUCKY_BOX || type === MsgPayloadType.TRANSFER) {
    return true;
  }
  return false;
}

export function isPendingMessage(status: MessageStatus) {
  return [MessageStatus.Queued, MessageStatus.Encrypted].includes(status);
}

export function isSuccessMessage(status: MessageStatus) {
  return [MessageStatus.Sent, MessageStatus.Decrypted].includes(status);
}

export function isFailedEncryptMessage(status: MessageStatus) {
  return [MessageStatus.FailedEncrypt, MessageStatus.FailedDecrypt].includes(status);
}

export function isFailedSentMessage(status: MessageStatus) {
  return [
    MessageStatus.FailedEncrypt,
    MessageStatus.FailedSent,
    MessageStatus.FailedSendFile,
  ].includes(status);
}

export function isFailedMessage(status: MessageStatus) {
  return isFailedEncryptMessage(status) || isFailedSentMessage(status);
}

export function isForwardMessage(msg_action: MessageAction | undefined): boolean {
  if (!msg_action) {
    return false;
  }
  return (
    msg_action === MessageAction.FORWARD_NO_ENCRYPT ||
    msg_action === MessageAction.FORWARD_ENCRYPT
  );
}

export function mergeMsg(prevMsg: Msg, { reactions, ...nextMsg }: DeepPartial<Msg>) {
  const merged = deepMerge(prevMsg, nextMsg);
  // handle emoji reactions
  if (reactions) {
    if (!merged.emoji_reaction) merged.emoji_reaction = [];
    reactions.forEach(({ emoji_id, user_id, opt_type, op_at }) => {
      // add
      if (opt_type === 1) {
        if (!merged.emoji_reaction.find((item) => item.emoji_id === emoji_id))
          merged.emoji_reaction.push({ emoji_id, reaction_log: [] });
        const reaction = merged.emoji_reaction.find((item) => item.emoji_id === emoji_id);
        if (reaction?.reaction_log?.find((item) => item.user_id === user_id)) return;
        reaction?.reaction_log.push({ user_id, operation_at: op_at });
      }
      // remove
      if (opt_type === 2) {
        if (!merged.emoji_reaction.find((item) => item.emoji_id === emoji_id)) return;
        const reaction = merged.emoji_reaction.find((item) => item.emoji_id === emoji_id);
        if (!reaction?.reaction_log.find((item) => item.user_id === user_id)) return;
        reaction?.reaction_log.splice(
          reaction?.reaction_log.findIndex((item) => item.user_id === user_id),
          1
        );
      }
    });
  }

  return merged;
}
